import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { api } from '../../lib/api';
import { formatPrice } from '../../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { DateTimeRangePicker } from '../../components/ui/date-time-range-picker';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, RefreshCw, Eye, Search, Edit, Printer } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import ExchangeCarInvoice from '../../components/ExchangeCarInvoice';

type DateRange = {
  from: Date | undefined;
  to?: Date | undefined;
}

interface ExchangeCar {
  id: number;
  personOneCarBrand: string;
  personOneCarModel: string;
  personOneCarYear: number;
  personOneCarPlateNo: string;
  personOneCarChassisNo?: string | null;
  personOneCarColor: string;
  personOneName: string;
  personOneIdNo: string;
  personOneAddress: string | null;
  personOneContact: string;
  personTwoCarBrand: string;
  personTwoCarModel: string;
  personTwoCarYear: number;
  personTwoCarPlateNo: string;
  personTwoCarChassisNo?: string | null;
  personTwoCarColor: string;
  personTwoName: string;
  personTwoIdNo: string;
  personTwoAddress: string | null;
  personTwoContact: string;
  amountOwed: number;
  paidAmount: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface ExchangeCarsResponse {
  exchangeCars: ExchangeCar[];
  pagination: PaginationData;
}

export default function ExchangeCarsIndex() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [exchangeCars, setExchangeCars] = useState<ExchangeCar[]>([]);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [selectedExchange, setSelectedExchange] = useState<ExchangeCar | null>(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const invoiceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchExchangeCars();
  }, [dateRange, currentPage, debouncedSearchTerm]);

  const fetchExchangeCars = async () => {
    try {
      setTableLoading(true);
      const params = new URLSearchParams();
      if (dateRange?.from) params.append('fromDate', dateRange.from.toISOString());
      if (dateRange?.to) params.append('toDate', dateRange.to.toISOString());
      if (debouncedSearchTerm.trim()) params.append('search', debouncedSearchTerm.trim());
      params.append('page', currentPage.toString());
      params.append('limit', itemsPerPage.toString());

      const data = await api.get(`/api/exchange-cars?${params.toString()}`) as ExchangeCarsResponse | null;
      setExchangeCars(data?.exchangeCars ?? []);
      setPagination(data?.pagination ?? null);
    } catch (error) {
      console.error('Error fetching exchange cars:', error);
      setExchangeCars([]);
      setPagination(null);
    } finally {
      setTableLoading(false);
    }
  };

  const clearFilters = () => {
    setDateRange(undefined);
    setSearchTerm('');
    setCurrentPage(1);
  };

  const goToPage = (page: number) => setCurrentPage(page);
  const goToNextPage = () => { if (pagination?.hasNextPage) setCurrentPage(currentPage + 1); };
  const goToPreviousPage = () => { if (pagination?.hasPrevPage) setCurrentPage(currentPage - 1); };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const handlePrint = useReactToPrint({
    contentRef: invoiceRef,
    documentTitle: `Exchange Car Invoice - ${selectedExchange?.personOneCarBrand} ${selectedExchange?.personOneCarModel} ↔ ${selectedExchange?.personTwoCarBrand} ${selectedExchange?.personTwoCarModel}`,
    pageStyle: `@page { size: A4; margin: 0; } @media print { body { -webkit-print-color-adjust: exact; color-adjust: exact; } }`
  });

  const handlePrintInvoice = (exchange: ExchangeCar) => {
    setSelectedExchange(exchange);
    setShowInvoice(true);
    setTimeout(() => {
      handlePrint();
      setTimeout(() => setShowInvoice(false), 1000);
    }, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-48 rounded-lg" />
          <span className="text-sm text-slate-500">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('exchangeCarHistory.title')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t('exchangeCarHistory.subtitle')}</p>
          </div>
          <Link to="/exchange-cars/add">
            <Button variant="outline" size="sm" className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('exchangeCarHistory.backToExchange')}
            </Button>
          </Link>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <CardTitle className="text-lg font-semibold text-slate-900">{t('exchangeCarHistory.exchangeHistory')}</CardTitle>
              <Badge variant="secondary" className="bg-slate-100 text-slate-800">
                {pagination?.totalCount || 0} {t('exchangeCarHistory.exchanges')}
              </Badge>
            </div>
            <CardDescription>{t('exchangeCarHistory.exchangeDescription')}</CardDescription>

            <div className="mt-6">
              <div className="flex flex-col lg:flex-row gap-6 mb-4">
                <div className="flex-1 lg:max-w-md">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">{t('exchangeCarHistory.search')}</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                    <Input
                      type="text"
                      placeholder={t('exchangeCarHistory.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full rounded-lg"
                    />
                  </div>
                  {searchTerm && (
                    <p className="text-xs text-slate-500 mt-1">{t('exchangeCarHistory.searchingFor')}: "{searchTerm}"</p>
                  )}
                </div>

                <div className="w-80">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">{t('exchangeCarHistory.dateRange')}</Label>
                  <DateTimeRangePicker value={dateRange} onValueChange={setDateRange} className="w-full" />
                  {dateRange?.from && dateRange?.to && (
                    <p className="text-xs text-slate-500 mt-1">
                      {Math.ceil((dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24))} {t('exchangeCarHistory.daysSelected')}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={clearFilters} variant="outline" className="w-full md:w-auto border-slate-200 text-slate-700 hover:bg-slate-50">
                  {t('exchangeCarHistory.clearFilters')}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {exchangeCars.length === 0 && !tableLoading ? (
              <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-500 text-lg">
                  {dateRange?.from && dateRange?.to ? t('exchangeCarHistory.noExchangesFound') : t('exchangeCarHistory.noExchanges')}
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  {dateRange?.from && dateRange?.to ? t('exchangeCarHistory.tryAdjusting') : t('exchangeCarHistory.startExchanging')}
                </p>
                <Link to="/exchange-cars/add" className="mt-4 inline-block">
                  <Button className="bg-slate-900 hover:bg-slate-800 text-white">{t('exchangeCarHistory.exchangeFirstCar')}</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('exchangeCarHistory.exchangeId')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('exchangeCarHistory.personOne')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('exchangeCarHistory.personOneCar')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('exchangeCarHistory.personTwo')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('exchangeCarHistory.personTwoCar')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('exchangeCarHistory.amountOwed')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm">{t('exchangeCarHistory.exchangeDate')}</TableHead>
                        <TableHead className="text-slate-600 font-medium text-sm text-right">{t('exchangeCarHistory.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tableLoading ? (
                        Array.from({ length: 5 }).map((_, index) => (
                          <TableRow key={`loading-${index}`}>
                            {Array.from({ length: 8 }).map((_, i) => (
                              <TableCell key={i}><Skeleton className="h-4 w-24" /></TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : (
                        exchangeCars.map((exchange) => (
                          <TableRow key={exchange.id} className="hover:bg-slate-50">
                            <TableCell className="text-sm text-slate-700 font-medium">
                              <span className="bg-slate-100 text-slate-900 px-2 py-1 rounded text-sm font-mono tabular-nums">#{exchange.id}</span>
                            </TableCell>
                            <TableCell className="text-sm text-slate-700">
                              <div><div className="font-medium">{exchange.personOneName}</div><div className="text-slate-500">{exchange.personOneContact}</div></div>
                            </TableCell>
                            <TableCell className="text-sm text-slate-700">
                              <div><div className="font-medium">{exchange.personOneCarBrand} {exchange.personOneCarModel}</div><div className="text-slate-500">{exchange.personOneCarYear} - {exchange.personOneCarColor}</div></div>
                            </TableCell>
                            <TableCell className="text-sm text-slate-700">
                              <div><div className="font-medium">{exchange.personTwoName}</div><div className="text-slate-500">{exchange.personTwoContact}</div></div>
                            </TableCell>
                            <TableCell className="text-sm text-slate-700">
                              <div><div className="font-medium">{exchange.personTwoCarBrand} {exchange.personTwoCarModel}</div><div className="text-slate-500">{exchange.personTwoCarYear} - {exchange.personTwoCarColor}</div></div>
                            </TableCell>
                            <TableCell className="text-sm">
                              {exchange.amountOwed > 0 || exchange.paidAmount > 0 ? (
                                <div className="space-y-1">
                                  {exchange.amountOwed > 0 && <div><span className="font-semibold text-red-600 tabular-nums">{formatPrice(exchange.amountOwed)}</span></div>}
                                  {exchange.paidAmount > 0 && <div><span className="font-semibold text-emerald-600 tabular-nums">{formatPrice(exchange.paidAmount)}</span></div>}
                                  {exchange.amountOwed > 0 && (exchange.amountOwed - (exchange.paidAmount || 0)) > 0 && <div><span className="font-semibold text-slate-700 tabular-nums">{formatPrice(exchange.amountOwed - (exchange.paidAmount || 0))}</span></div>}
                                </div>
                              ) : <span className="text-slate-400">-</span>}
                            </TableCell>
                            <TableCell className="text-sm text-slate-500 tabular-nums">{formatDate(exchange.createdAt)}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button size="sm" variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50" onClick={() => navigate(`/exchange-cars/${exchange.id}`)}>
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="outline" className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800" onClick={() => navigate(`/exchange-cars/${exchange.id}/edit`)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => handlePrintInvoice(exchange)}>
                                  <Printer className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {pagination && pagination.totalPages > 1 && !tableLoading && (
                  <div className="flex items-center justify-between mt-6">
                    <div className="text-sm text-slate-500">
                      {t('exchangeCarHistory.showing')} {((pagination.currentPage - 1) * pagination.limit) + 1} {t('exchangeCarHistory.to')} {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} {t('exchangeCarHistory.of')} {pagination.totalCount} {t('exchangeCarHistory.results')}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button variant="outline" size="sm" onClick={goToPreviousPage} disabled={!pagination.hasPrevPage} className="border-slate-200 text-slate-700 hover:bg-slate-50">
                        {t('exchangeCarHistory.previous')}
                      </Button>
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                          <Button key={page} variant={pagination.currentPage === page ? "default" : "outline"} size="sm" onClick={() => goToPage(page)}
                            className={`w-8 h-8 p-0 rounded-lg ${pagination.currentPage === page ? 'bg-slate-900 hover:bg-slate-800' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
                            {page}
                          </Button>
                        ))}
                      </div>
                      <Button variant="outline" size="sm" onClick={goToNextPage} disabled={!pagination.hasNextPage} className="border-slate-200 text-slate-700 hover:bg-slate-50">
                        {t('exchangeCarHistory.next')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {showInvoice && selectedExchange && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden' }}>
          <ExchangeCarInvoice ref={invoiceRef} exchangeCar={selectedExchange} isReprint={true} />
        </div>
      )}
    </div>
  );
}
